import { Observable } from 'rxjs';
import { htmlFromDom } from '../../helpers/dom/htmlFromDom';
import { cleanHtmlToDom } from '../../helpers/dom/htmlToDom';
import { replaceAllChildren } from '../../helpers/dom/node';
import { markdownAstFromDom } from '../../helpers/markdown/markdownAstFromDom';
import { markdownAstToDom } from '../../helpers/markdown/markdownAstToDom';
import { markdownFromDom } from '../../helpers/markdown/markdownFromDom';
import { markdownToDom } from '../../helpers/markdown/markdownToDom';
import { sanitizeHtml } from '../../helpers/sanitizeHtml';
import { createConnectingService } from '../base/connectingService';
import { BaseService, ServiceLike } from '../base/service';
import { HtmlService, HtmlServiceWaitForChangesFunction } from '../html/htmlService';
import { MarkdownService } from '../rich/markdownService';
import { ElementService } from './elementService';

export function createElementMarkdownHtmlService(
  element$: Observable<Element | null>,
  waitForChanges: HtmlServiceWaitForChangesFunction,
  markdownService: MarkdownService,
): HtmlService {
  return createConnectingService<Element, HtmlService>(
    element$,
    () => new MarkdownHtmlService(waitForChanges, markdownService),
    element => new ElementMarkdownHtmlService(element, waitForChanges, markdownService),
  );
}

/** Implements an HTML service by feeding the DOM content through a Markdown AST. */
export class ElementMarkdownHtmlService extends ElementService<Element> implements HtmlService {
  constructor(
    element: Element,
    waitForChanges: HtmlServiceWaitForChangesFunction,
    ...dependencies: ServiceLike[]
  ) {
    super(element, ...dependencies);

    this.waitForChanges = waitForChanges;
  }

  readonly waitForChanges: HtmlServiceWaitForChangesFunction;

  get cleanHtml() {
    const markdownAst = markdownAstFromDom(this.element);
    return htmlFromDom(document => markdownAstToDom(document, markdownAst));
  }

  get html() {
    return this.cleanHtml;
  }

  setDirtyHtml(dirtyHtml: string) {
    this.setCleanHtml(sanitizeHtml(dirtyHtml));
  }

  setCleanHtml(cleanHtml: string) {
    const markdownAst = cleanHtmlToDom(cleanHtml, parentNode => markdownAstFromDom(parentNode));
    const domFragment = markdownAstToDom(this.document, markdownAst);
    replaceAllChildren(this.element, domFragment);
  }
}

/** Implements an HTML service by converting to/from Markdown. */
export class MarkdownHtmlService extends BaseService implements HtmlService {
  constructor(waitForChanges: HtmlServiceWaitForChangesFunction, markdownService: MarkdownService) {
    super(markdownService);

    this.waitForChanges = waitForChanges;
    this.markdownService = markdownService;
  }

  readonly waitForChanges: HtmlServiceWaitForChangesFunction;
  private readonly markdownService: MarkdownService;

  get cleanHtml() {
    const { markdown } = this.markdownService;
    return htmlFromDom(document => markdownToDom(document, markdown));
  }

  get html() {
    return this.cleanHtml;
  }

  setDirtyHtml(dirtyHtml: string) {
    this.setCleanHtml(sanitizeHtml(dirtyHtml));
  }

  setCleanHtml(cleanHtml: string) {
    const markdown = cleanHtmlToDom(cleanHtml, parentNode => markdownFromDom(parentNode));
    this.markdownService.setMarkdown(markdown);
  }
}
